import React, { Fragment, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/react-hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import emailIcon from "@iconify/icons-mdi/email";
import keyIcon from "@iconify/icons-mdi/key";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import { LOGIN } from "my-hr/graphql";
import { OcErrorBoundary, OcLoading } from "oc/components";
import { useOcClientState } from "oc/hooks";
import useOcProperty from "oc/hooks/use-property";

const validator = new Validator();

interface MyHrLoginFormProps {
  onSubmit: (loginResult: any) => void;
  buttonLabel: string;
}

export const MyHrLoginForm: React.FC<MyHrLoginFormProps> = ({
  onSubmit,
  buttonLabel,
}) => {
  const { t } = useTranslation();

  const schema = {
    email: {
      type: "email",
      messages: {
        email: t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT"),
        emailEmpty: t("OC_ERROR_MESSAGE_EMAIL_IS_REQUIRED"),
      },
    },
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "Nem megfelelő jelszó!",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [email, setEmail] = useOcClientState<string>("loginEmail", "");
  const [emailError, setEmailError] = useOcClientState<boolean>(
    "loginEmailError",
    false,
  );
  const [emailErrorText, setEmailErrorText] = useOcClientState<string>(
    "loginEmailErrorText",
    "",
  );
  const [password, setPassword] = useOcClientState<string>("loginPassword", "");
  const [showPassword, setShowPassword] = useState(false);

  const [passwordError, setPasswordError] = useOcClientState<boolean>(
    "loginPasswordError",
    false,
  );
  const [passwordErrorText, setPasswordErrorText] = useOcClientState<string>(
    "loginPasswordErrorText",
    "",
  );

  const { data: passwordVisibilityData } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const [login, { loading, data: loginData, error: loginError }] = useLazyQuery(LOGIN, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (loginData?.login) {
      onSubmit(loginData.login);
    }
  }, [loginData, onSubmit]);

  useEffect(() => {
    if (loginError?.graphQLErrors) {
      loginError.graphQLErrors.forEach((item) => {
        if (item.message === "No user found!") {
          setEmailErrorText(t("MY_HR_ERROR_MESSAGE_NO_USER_FOUND"));
          setEmailError(true);
        }
        if (item.message === "Wrong password!") {
          setPasswordErrorText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
          setPasswordError(true);
        }
        if (item.message === "User account has expired!") {
          setEmailErrorText(t("MY_HR_ERROR_MESSAGE_ACCOUNT_HAS_EXPIRED"));
          setEmailError(true);
        }
        if (item.message === "No valid legal contract found!") {
          setEmailErrorText(t("MY_HR_ERROR_MESSAGE_NO_CONTRACT_FOUND"));
          setEmailError(true);
        }
      });
    }
  }, [loginError, t, setEmailErrorText, setEmailError, setPasswordErrorText, setPasswordError]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailParsed =
      email ||
      (document.getElementById("email") as HTMLInputElement | null)?.value;
    const passwordParsed =
      password ||
      (document.getElementById("password") as HTMLInputElement | null)?.value;

    let isValidated = check({ email: emailParsed, password: passwordParsed });

    if (isValidated instanceof Promise) {
      isValidated = await isValidated;
    }

    if (isValidated !== true) {
      (isValidated as any[]).forEach((item) => {
        if (item.field === "email") {
          setEmailErrorText(item.message);
          setEmailError(true);
        }
        if (item.field === "password") {
          setPasswordErrorText(item.message);
          setPasswordError(true);
        }
      });
      return;
    }

    login({ variables: { email: emailParsed, password: passwordParsed } });
  };
  useEffect(() => {
    const handleClear = () => {
      setEmail("");
      setEmailError(false);
      setEmailErrorText("");
      setPassword("");
      setPasswordError(false);
      setPasswordErrorText("");
    };
    return () => {
      handleClear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  useLayoutEffect(() => {
    const handleHiddenState = () => {
      if (document.hidden) {
        // Az oldal láthatatlan
      } else {
        setEmail(
          (document.getElementById("email") as HTMLInputElement | null)
            ?.value ?? "",
        );
        setPassword(
          (document.getElementById("password") as HTMLInputElement | null)
            ?.value ?? "",
        );
      }
    };
    window.addEventListener("visibilitychange", handleHiddenState);
    return () =>
      window.removeEventListener("visibilitychange", handleHiddenState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <OcErrorBoundary>
        <OcLoading show={loading} />
        <form noValidate autoComplete="off" onSubmit={handleLogin}>
          <Box paddingTop="18px" paddingBottom="14px">
            <TextField
              id="email"
              defaultValue={email}
              onChange={handleEmailChange}
              label={t("MY_HR_EMAIL")}
              variant="outlined"
              fullWidth
              error={emailError}
              onFocus={() => {
                setEmailErrorText("");
                setEmailError(false);
              }}
              helperText={emailError && emailErrorText}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon
                      height="24px"
                      icon={emailIcon}
                      color="#919699"
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box paddingTop="18px" paddingBottom="14px">
            <TextField
              id="password"
              defaultValue={password}
              onChange={(e) => setPassword(e.target.value)}
              label={t("MY_HR_PASSWORD")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              fullWidth
              helperText={passwordErrorText}
              error={passwordError}
              onFocus={() => {
                setPasswordErrorText("");
                setPasswordError(false);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon height="24px" icon={keyIcon} color="#919699" />
                    <Box width="8px" />
                    {passwordVisibilityData === "true" && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        style={{
                          backgroundColor: "#F1F3F4",
                          height: "48px",
                          width: "48px",
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon
                            style={{ height: "24px", width: "24px" }}
                          />
                        ) : (
                          <VisibilityIcon
                            style={{ height: "24px", width: "24px" }}
                          />
                        )}
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box paddingTop="18px">
            <Button
              id="loginButton"
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disableElevation
            >
              {buttonLabel}
            </Button>
          </Box>
        </form>
      </OcErrorBoundary>
    </Fragment>
  );
};

export default MyHrLoginForm;
